import { dropByCacheKey, getCachingKeys, refreshByCacheKey } from 'react-router-cache-route';

// 手动缓存白名单：业务运行时调用 addRouteCache 后会加入这里。
const cacheRouteNames = new Set();
// 手动缓存参数：保存 addRouteCache(path, options) 传入的配置。
const cacheRouteOptions = {};
// 指定缓存刷新版本：版本变化会让对应 CacheRoute 重新创建。
const cacheRouteRefreshVersions = {};
// 手动关闭名单：用于临时关闭 route.cache = true 的路由缓存。
const disabledCacheRouteNames = new Set();
// 路由层订阅白名单变化，用于 add/remove 后触发重新渲染。
const listeners = [];
let shouldRefreshNextRecover = false;

/**
 * 统一兼容 path 字符串、location 对象、route 配置对象。
 * 缓存名称只取 pathname，避免 query/hash 变化导致无法删除同一个缓存。
 */
function getPathname(name) {
  if (!name) return '';

  if (typeof name == 'object') {
    return name.cacheName || name.name || name.pathname || name.path || '';
  }

  const value = `${name}`.trim();
  if (!value) return '';

  return value.split('#')[0].split('?')[0];
}

function normalizeRouteCacheName(name) {
  return getPathname(name);
}

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj || {}, key);
}

function getValueByKeys(obj, keys, defaultValue) {
  for (let i = 0; i < keys.length; i += 1) {
    if (hasOwn(obj, keys[i])) {
      return obj[keys[i]];
    }
  }

  return defaultValue;
}

function getCacheOptions(item, options) {
  if (options) return options;
  if (!item || typeof item != 'object') return {};

  return {
    cacheWhen: item.cacheWhen,
    cacheMultiple: item.cacheMultiple,
    cacheSaveScrollPosition: item.cacheSaveScrollPosition,
    saveScrollPosition: item.saveScrollPosition
  };
}

function isSameCacheOptions(prevOptions, nextOptions) {
  return JSON.stringify(prevOptions || {}) == JSON.stringify(nextOptions || {});
}

/**
 * 通知 RouterView 重新渲染，使最新白名单立即生效。
 */
function emitChange() {
  listeners.slice().forEach((listener) => {
    listener(Array.from(cacheRouteNames));
  });
}

/**
 * 手动增加缓存路由。
 * @param {String|Object|Array} name 路由 path/name/cacheName，支持数组批量增加。
 * @param {Object} options 手动缓存参数，如 cacheSaveScrollPosition/cacheWhen/cacheMultiple。
 */
function addRouteCache(name, options) {
  const names = Array.isArray(name) ? name : [name];
  let changed = false;

  names.forEach((item) => {
    const routeName = normalizeRouteCacheName(item);
    if (!routeName) return;

    disabledCacheRouteNames.delete(routeName);

    if (!cacheRouteNames.has(routeName)) {
      cacheRouteNames.add(routeName);
      changed = true;
    }

    const nextOptions = getCacheOptions(item, options);
    if (!isSameCacheOptions(cacheRouteOptions[routeName], nextOptions)) {
      cacheRouteOptions[routeName] = nextOptions;
      changed = true;
    }
  });

  if (changed) emitChange();
}

/**
 * 手动删除单个或多个缓存路由。
 * 删除白名单的同时调用 dropByCacheKey，清理已经存在的缓存实例。
 * @param {String|Object|Array} name 路由 path/name/cacheName，支持数组批量删除。
 */
function removeRouteCache(name) {
  const names = Array.isArray(name) ? name : [name];
  let changed = false;

  names.forEach((item) => {
    const routeName = normalizeRouteCacheName(item);
    if (!routeName) return;

    if (cacheRouteNames.delete(routeName)) {
      changed = true;
    }

    if (!disabledCacheRouteNames.has(routeName)) {
      disabledCacheRouteNames.add(routeName);
      changed = true;
    }

    delete cacheRouteOptions[routeName];
    dropByCacheKey(routeName);
  });

  if (changed) emitChange();
}

/**
 * 刷新指定缓存路由，让对应页面重新 mount。
 */
function refreshRouteCache(name) {
  const names = Array.isArray(name) ? name : [name];
  let changed = false;

  names.forEach((item) => {
    const routeName = normalizeRouteCacheName(item);
    if (!routeName) return;

    cacheRouteRefreshVersions[routeName] = (cacheRouteRefreshVersions[routeName] || 0) + 1;
    changed = true;
    refreshByCacheKey(routeName);
  });

  if (changed) emitChange();
}

/**
 * 标记下一次返回到缓存页面时刷新该页面。
 * 适合“不确定上一页是哪一个，但返回后希望上一页重新执行 componentDidMount”的场景。
 */
function refreshOnNextRecover() {
  shouldRefreshNextRecover = true;
}

function consumeRefreshOnNextRecover(name) {
  if (!shouldRefreshNextRecover) return false;

  shouldRefreshNextRecover = false;
  setTimeout(() => {
    refreshRouteCache(name);
  }, 0);
  return true;
}

function goBackWithRefresh(name) {
  if (name) {
    refreshRouteCache(name);
    return;
  }

  refreshOnNextRecover();
}

/**
 * 判断某个路由当前是否允许缓存。
 */
function hasRouteCache(name) {
  const routeName = normalizeRouteCacheName(name);
  return !!routeName && cacheRouteNames.has(routeName);
}

/**
 * 判断路由最终是否启用缓存。
 * route.cache = true 可以在路由层默认开启，removeRouteCache 可以临时关闭。
 */
function isRouteCacheEnabled(route) {
  const routeName = getRouteCacheName(route);
  if (!routeName || disabledCacheRouteNames.has(routeName)) return false;

  return !!(route.cache || cacheRouteNames.has(routeName));
}

/**
 * 合并路由配置和运行时 addRouteCache 传入的缓存参数。
 */
function getRouteCacheConfig(route) {
  const routeName = getRouteCacheName(route);
  const options = cacheRouteOptions[routeName] || {};

  return {
    cacheKey: routeName,
    when: getValueByKeys(options, ['cacheWhen', 'when'], route.cacheWhen || 'always'),
    multiple: getValueByKeys(options, ['cacheMultiple', 'multiple'], route.cacheMultiple || false),
    saveScrollPosition: getValueByKeys(
      options,
      ['cacheSaveScrollPosition', 'saveScrollPosition'],
      route.cacheSaveScrollPosition !== false
    )
  };
}

/**
 * 获取手动登记的缓存路由白名单。
 */
function getRouteCacheNames() {
  return Array.from(cacheRouteNames);
}

function getRouteCacheVersion(name) {
  const routeName = normalizeRouteCacheName(name);
  return routeName ? cacheRouteRefreshVersions[routeName] || 0 : 0;
}

/**
 * 订阅白名单变更。仅供路由层使用，业务通常不需要直接调用。
 */
function subscribeRouteCache(listener) {
  if (typeof listener != 'function') return () => {};

  listeners.push(listener);

  return () => {
    const index = listeners.indexOf(listener);
    if (index > -1) {
      listeners.splice(index, 1);
    }
  };
}

/**
 * 从路由配置中计算缓存名称：cacheName > name > path。
 */
function getRouteCacheName(route) {
  return normalizeRouteCacheName(route && (route.cacheName || route.name || route.path));
}

const routeCache = {
  addRouteCache,
  removeRouteCache,
  hasRouteCache,
  isRouteCacheEnabled,
  getRouteCacheConfig,
  getRouteCacheName,
  getRouteCacheNames,
  getRouteCacheVersion,
  getCachingKeys,
  refreshRouteCache,
  refreshOnNextRecover,
  consumeRefreshOnNextRecover,
  goBackWithRefresh,
  subscribeRouteCache
};

export {
  addRouteCache,
  removeRouteCache,
  hasRouteCache,
  isRouteCacheEnabled,
  getRouteCacheConfig,
  getRouteCacheName,
  getRouteCacheNames,
  getRouteCacheVersion,
  getCachingKeys,
  refreshRouteCache,
  refreshOnNextRecover,
  consumeRefreshOnNextRecover,
  goBackWithRefresh,
  subscribeRouteCache
};

export default routeCache;



// WEBPACK FOOTER //
// ./src/router/routeCache.js